import { baseService } from "../base";

// Types for QuickBooks invoice
export interface StorageInvoiceRequest {
  clientId: string;
  month: string;
  year: number;
  averageCubicFeet: number;
  cubicFootRate: number;
  includeInsurance: boolean;
  insuranceRate?: number;
  inventoryValue?: number;
}

export interface QuickBooksInvoiceResponse {
  Invoice?: {
    Id: string;
    DocNumber: string;
    Balance: number;
    TotalAmt: number;
  };
  success: boolean;
  message?: string;
}

export interface ServiceInvoiceRequest {
  serviceTicketId: string;
}

export interface QuickBooksConnectionStatus {
  isConnected: boolean;
  companyName?: string;
  realmId?: string;
  lastSyncTime?: string;
  tokenExpiresAt?: string;
  error?: string;
  authUrl?: string;
}

export interface QuickBooksTokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  x_refresh_token_expires_in: number;
  id_token: string;
  realmId?: string;
}

// Get QuickBooks OAuth authorization URL
export const getQuickBooksAuthUrl = async (state?: string): Promise<{ authUrl: string }> => {
  const { data } = await baseService.get('/api/quickbooks/auth/url', {
    params: { state }
  });
  return data;
};

// Get QuickBooks OAuth authorization URL with password verification
export const getQuickBooksAuthUrlWithPassword = async (password: string): Promise<{ authUrl: string }> => {
  const { data } = await baseService.post('/api/quickbooks/auth/url/verify', { password });
  return data;
};

// Get QuickBooks OAuth token with password verification
export const getQuickBooksTokenWithPassword = async (password: string): Promise<QuickBooksTokenResponse> => {
  const { data } = await baseService.post('/api/quickbooks/auth/token/verify', { password });
  return data;
};

// Check QuickBooks connection status
export const getQuickBooksConnectionStatus = async (): Promise<QuickBooksConnectionStatus> => {
  const { data } = await baseService.get('/api/quickbooks/connection/status');
  return data;
};

// Create a storage invoice in QuickBooks
export const createStorageInvoice = async (
  request: StorageInvoiceRequest
): Promise<QuickBooksInvoiceResponse> => {
  const { data } = await baseService.post('/api/quickbooks/storage/invoices', request);
  return data;
};

// Create a service invoice in QuickBooks
export const createServiceInvoice = async (
  request: ServiceInvoiceRequest
): Promise<QuickBooksInvoiceResponse> => {
  const { data } = await baseService.post('/api/quickbooks/service/invoices', request);
  return data;
};

// Sync invoice status from QuickBooks
export const syncQuickBooksInvoiceStatus = async (
  invoiceId: string
): Promise<QuickBooksInvoiceResponse> => {
  const { data } = await baseService.post(`/api/quickbooks/invoices/${invoiceId}/sync`);
  return data;
};

// Get payment link for invoice
export const getQuickBooksPaymentLink = async (
  invoiceId: string
): Promise<{ paymentUrl: string }> => {
  const { data } = await baseService.get(`/api/quickbooks/invoices/${invoiceId}/paymentlink`);
  return data;
};

// Get invoice details
export const getQuickBooksInvoice = async (
  invoiceId: string
): Promise<QuickBooksInvoiceResponse> => {
  const { data } = await baseService.get(`/api/quickbooks/invoices/${invoiceId}`);
  return data;
};

// Get invoice PDF
export const getInvoicePdf = async (invoiceId: string): Promise<Blob> => {
  const response = await baseService.get(`/api/quickbooks/invoices/${invoiceId}/pdf`, {
    responseType: 'blob',
  });
  return response.data;
};

// Disconnect from QuickBooks
export const disconnectQuickBooks = async (): Promise<{ success: boolean; message: string }> => {
  const { data } = await baseService.post('/api/quickbooks/disconnect');
  return data;
};

